import { usePermissions } from '../../hooks/usePermissions'
import { AlertCircle, Lock } from 'lucide-react'

/**
 * Component to conditionally render content based on permissions
 * @param {Object} props - Component props
 * @param {string} props.permission - Single permission to check
 * @param {Array<string>} props.permissions - Array of permissions (any)
 * @param {Array<string>} props.allPermissions - Array of permissions (all required)
 * @param {React.ReactNode} props.children - Content to render if permission granted
 * @param {React.ReactNode} props.fallback - Content to render if permission denied
 * @param {boolean} props.showFallback - Whether to show fallback content
 * @param {string} props.fallbackType - Type of fallback ('hidden', 'disabled', 'message')
 * @param {string} props.fallbackMessage - Custom fallback message
 * @returns {React.ReactNode} - Rendered content
 */
const PermissionGate = ({
  permission,
  permissions,
  allPermissions,
  children,
  fallback,
  showFallback = true,
  fallbackType = 'hidden',
  fallbackMessage
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading, metadata } = usePermissions()

  // While loading, show children (optimistic rendering)
  if (isLoading) {
    return children
  }

  let hasAccess = false

  // Check permissions based on props
  if (permission) {
    hasAccess = hasPermission(permission)
  } else if (permissions && permissions.length > 0) {
    hasAccess = hasAnyPermission(permissions)
  } else if (allPermissions && allPermissions.length > 0) {
    hasAccess = hasAllPermissions(allPermissions)
  } else {
    // No permissions specified, grant access
    hasAccess = true
  }

  // If access granted, render children
  if (hasAccess) {
    return children
  }

  // If no fallback should be shown, return null
  if (!showFallback) {
    return null
  }

  // If custom fallback provided, use it
  if (fallback) {
    return fallback
  }

  // Generate default fallback based on type
  switch (fallbackType) {
    case 'hidden':
      return null

    case 'disabled':
      return (
        <div className="opacity-50 pointer-events-none">
          {children}
        </div>
      )

    case 'message':
      const permissionName = permission 
        ? metadata?.[permission]?.name || permission
        : permissions?.map(p => metadata?.[p]?.name || p).join(' or ')
        || allPermissions?.map(p => metadata?.[p]?.name || p).join(' and ')
        || 'required permissions'

      return (
        <div className="flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
          <div className="text-center">
            <Lock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
              Access Restricted
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              {fallbackMessage || `Requires: ${permissionName}`}
            </p>
          </div>
        </div>
      )

    default:
      return null
  }
}

/**
 * Higher-order component to wrap components with permission checking
 * @param {React.Component} Component - Component to wrap
 * @param {Object} permissionConfig - Permission configuration
 * @returns {React.Component} - Wrapped component
 */
export const withPermission = (Component, permissionConfig) => {
  return (props) => (
    <PermissionGate {...permissionConfig}>
      <Component {...props} />
    </PermissionGate>
  )
}

/**
 * Component to show permission-based error message
 * @param {Object} props - Component props
 * @param {string} props.permission - Permission that was required
 * @param {string} props.message - Custom error message
 * @returns {React.ReactNode} - Error message component
 */
export const PermissionDenied = ({ permission, message }) => {
  const { metadata } = usePermissions()
  
  const permissionName = metadata?.[permission]?.name || permission
  const permissionDescription = metadata?.[permission]?.description

  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="text-center max-w-md mx-auto">
        <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Access Denied
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {message || `You don't have permission to access this feature.`}
        </p>
        {permissionName && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Required Permission: {permissionName}
            </p>
            {permissionDescription && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {permissionDescription}
              </p>
            )}
          </div>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
          Contact your administrator if you believe this is an error.
        </p>
      </div>
    </div>
  )
}

export default PermissionGate